// CRM Slice 1 — TypeScript interfaces mirroring the frozen contract DTOs
// (docs/superpowers/plans/2026-06-19-crm-pipeline-slice1-contract.md).
// Enums are string-literal unions whose members match the backend enum names verbatim.

export type LeadSource = 'WEBSITE' | 'REFERRAL' | 'COLD_CALL' | 'EVENT' | 'OTHER';

export type LeadStatus = 'NEW' | 'CONTACTED' | 'QUALIFIED' | 'CONVERTED' | 'DISQUALIFIED';

export type OpportunityStage =
  | 'NEW' | 'QUALIFIED' | 'PROPOSAL' | 'NEGOTIATION' | 'WON' | 'LOST';

export type ActivityOwnerType = 'LEAD' | 'OPPORTUNITY';

export type ActivityType = 'CALL' | 'EMAIL' | 'MEETING' | 'NOTE';

// Ordered stages (matches OpportunityStage NEW(0)..LOST(5) in the backend).
export const OPPORTUNITY_STAGES: OpportunityStage[] = [
  'NEW', 'QUALIFIED', 'PROPOSAL', 'NEGOTIATION', 'WON', 'LOST',
];

// Classification helpers — mirror the backend isOpen()/isWon()/isLost().
export const isOpenStage = (s: OpportunityStage): boolean => s !== 'WON' && s !== 'LOST';
export const isWonStage = (s: OpportunityStage): boolean => s === 'WON';
export const isLostStage = (s: OpportunityStage): boolean => s === 'LOST';

// Human labels for chips / column headers.
export const LEAD_SOURCE_LABELS: Record<LeadSource, string> = {
  WEBSITE: 'Website',
  REFERRAL: 'Referral',
  COLD_CALL: 'Cold Call',
  EVENT: 'Event',
  OTHER: 'Other',
};

export const LEAD_STATUS_LABELS: Record<LeadStatus, string> = {
  NEW: 'New',
  CONTACTED: 'Contacted',
  QUALIFIED: 'Qualified',
  CONVERTED: 'Converted',
  DISQUALIFIED: 'Disqualified',
};

export const OPPORTUNITY_STAGE_LABELS: Record<OpportunityStage, string> = {
  NEW: 'New',
  QUALIFIED: 'Qualified',
  PROPOSAL: 'Proposal',
  NEGOTIATION: 'Negotiation',
  WON: 'Won',
  LOST: 'Lost',
};

export const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  CALL: 'Call',
  EMAIL: 'Email',
  MEETING: 'Meeting',
  NOTE: 'Note',
};

// --- Lead (mirrors LeadDTO) ---
export interface Lead {
  id: string;
  name: string;
  companyName?: string;
  email?: string;
  phone?: string;
  source: LeadSource;
  status: LeadStatus;
  disqualifyReason?: string;
  convertedCustomerId?: string;
  convertedOpportunityId?: string;
  notes?: string;
  institutionId: string;
  createdAt: string;
  updatedAt: string;
}

// Mirrors LeadCreateDTO
export interface LeadCreateDTO {
  name: string;
  companyName?: string;
  email?: string;
  phone?: string;
  source?: LeadSource;
  notes?: string;
  institutionId: string;
}

// Mirrors ConvertLeadDTO — service rule: existingCustomerId != null OR customerName non-blank.
export interface ConvertLeadDTO {
  existingCustomerId?: string;
  customerName?: string;
  contactPerson?: string;
  email?: string;
  phone?: string;
  address?: string;
  taxRegistrationNo?: string;
  opportunityTitle?: string;
  estimatedAmount?: number;
  expectedCloseDate?: string;
}

// Mirrors LeadDisqualifyDTO
export interface LeadDisqualifyDTO {
  reason: string;
}

export interface LeadPage {
  content: Lead[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

// --- Opportunity (mirrors OpportunityDTO) ---
export interface Opportunity {
  id: string;
  title: string;
  customerId: string;
  customerName?: string;
  stage: OpportunityStage;
  estimatedAmount?: number;
  expectedCloseDate?: string;
  lostReason?: string;
  quotationId?: string;
  sourceLeadId?: string;
  notes?: string;
  institutionId: string;
  createdAt: string;
  updatedAt: string;
}

// Mirrors OpportunityCreateDTO
export interface OpportunityCreateDTO {
  title: string;
  customerId: string;
  estimatedAmount?: number;
  expectedCloseDate?: string;
  notes?: string;
  institutionId: string;
}

// Mirrors OpportunityStageChangeDTO
export interface OpportunityStageChangeDTO {
  stage: OpportunityStage;
  lostReason?: string;
}

// Mirrors OpportunityCardDTO
export interface OpportunityCard {
  id: string;
  title: string;
  customerName?: string;
  estimatedAmount?: number;
  expectedCloseDate?: string;
  stage: OpportunityStage;
  quotationId?: string;
  lastActivityAt?: string;
}

// Mirrors OpportunityBoardColumnDTO
export interface OpportunityBoardColumn {
  stage: OpportunityStage;
  cards: OpportunityCard[];
}

// Mirrors OpportunityBoardDTO
export interface OpportunityBoard {
  columns: OpportunityBoardColumn[];
}

export interface OpportunityPage {
  content: Opportunity[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

// --- Activity (mirrors ActivityDTO) ---
export interface Activity {
  id: string;
  ownerType: ActivityOwnerType;
  ownerId: string;
  type: ActivityType;
  note: string;
  occurredAt: string;
  createdBy?: string;
  createdAt: string;
}

// Mirrors ActivityCreateDTO — occurredAt null → backend sets now().
export interface ActivityCreateDTO {
  ownerType: ActivityOwnerType;
  ownerId: string;
  type: ActivityType;
  note: string;
  occurredAt?: string;
  institutionId: string;
}
